import React from 'react';

import Error from 'next/error';
import Head from 'next/head';

import _get from 'lodash/get';

import { withBonnetPage } from '@bonnet/next/page';
import { useRouterScroll } from '@bonnet/next/use-router-scroll';

import { pageNames } from '@atc/bonnet-paths';
import { useFeatures } from '@atc/features';
import { getScaledImageSource, getSrcSet } from '@atc/images';

import { breakpoints, imageSizes } from 'axl-config';

import getScaledPrimaryImg from '@/utilities/getScaledPrimaryImg';
import offsetCalc from '@/utilities/offSetScrollCalculation';
import useHashScroll from '@/utilities/useHashScroll';
import usePauseAudioEye from '@/utilities/usePauseAudioEye';
import { initializeVdpPageContext } from '@/utilities/vdpPageInitialization';

import expiredListingDuck from '@/ducks/expiredListingDuck.js';
import listingsAgentDuck from '@/ducks/listingsAgentDuck';
import srpResultsDuck from '@/ducks/srp/srpResultsDuck.js';
import srpReuseActiveStateDuck from '@/ducks/srp/srpReuseActiveStateDuck.js';
import fraudReportDuck from '@/ducks/vdp/fraudReportDuck.js';
import vdpActiveEmailListingDuck from '@/ducks/vdp/vdpActiveEmailListingDuck.js';
import vdpActiveInteractionDuck from '@/ducks/vdp/vdpActiveInteractionDuck.js';
import vdpAdsDuck from '@/ducks/vdp/vdpAdsDuck.js';
import vdpAvailableOffersDuck from '@/ducks/vdp/vdpAvailableOffersDuck.js';
import vdpContentDuck from '@/ducks/vdp/vdpContentDuck.js';
import vdpDealerDiffDuck from '@/ducks/vdp/vdpDealerDiffDuck.js';
import vdpDRDuck from '@/ducks/vdp/vdpDRDuck.js';
import vdpExtendedHomeDeliveryDuck from '@/ducks/vdp/vdpExtendedHomeDeliveryDuck.js';
import vdpModelInfoDuck from '@/ducks/vdp/vdpModelInfoDuck.js';
import vdpRatingReviewDuck from '@/ducks/vdp/vdpRatingReviewDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';
import vdpReviewPaginationDuck from '@/ducks/vdp/vdpReviewPaginationDuck.js';
import vdpSimilarDealerListingsDuck from '@/ducks/vdp/vdpSimilarDealerListingsDuck.js';
import vdpSimilarListingsDuck from '@/ducks/vdp/vdpSimilarListingsDuck.js';
import vdpVideoDuck from '@/ducks/vdp/vdpVideoDuck.js';

import atcAuthModule from '@/modules/AuthModule.js';
import CanonicalUrlModule from '@/modules/CanonicalUrlModule.js';
import CookieConsentModule from '@/modules/CookieConsentModule.js';
import ParsedQueryModule from '@/modules/ParsedQueryModule.js';
import PersonalizationEngineModule from '@/modules/PersonalizationEngineModule.js';
import SearchLinkModule from '@/modules/SearchLinkModule.js';

import CommonHeadContainer from '@/containers/CommonHeadContainer';
import PersonalizationEngineContainer from '@/containers/PersonalizationEngineContainer';
import { mainImageSizes } from '@/containers/vdp/MediaGallery/sizes';
import VDPAnalytics from '@/containers/vdp/VDPAnalytics';
import VdpPageEventsContainer from '@/containers/vdp/VdpPageEventsContainer';
import VehicleDetailsPageContainer from '@/containers/vdp/VehicleDetailsPageContainer';

import FordVehicleDetailsSchema from '@/components/FordVehicleDetailsSchema/index.js';

const vdpDucks = {
    fraudReportDuck,
    vdpActiveEmailListingDuck,
    vdpActiveInteractionDuck,
    vdpAdsDuck,
    vdpAvailableOffersDuck,
    vdpContentDuck,
    vdpDRDuck,
    vdpModelInfoDuck,
    vdpRatingReviewDuck,
    vdpResultsDuck,
    vdpReviewPaginationDuck,
    vdpSimilarListingsDuck,
    vdpSimilarDealerListingsDuck,
    vdpVideoDuck,
    vdpExtendedHomeDeliveryDuck,
    vdpDealerDiffDuck,
};

const { VEHICLE_DETAILS } = pageNames;

function VehicleDetailsPage({
    browser,
    currentUrl,
    images,
    statusCode,
    query,
}) {
    const {
        image_optimization: [enableImageOptimization],
        personalization_engine: [enablePersonalizationEngine],
    } = useFeatures([
        'image_optimization',
        'personalization_engine',
    ]);

    usePauseAudioEye();

    useRouterScroll();

    const scrollOffset = offsetCalc(20, 'sticky-header-vdp');
    useHashScroll({ enableSmoothScroll: true, offset: scrollOffset, delay: 750 });

    const primary = _get(images, 'primary', 0);
    const primaryImgSrc = _get(images, `sources[${primary}].src`, '');
    const verifiedImgSrc = enableImageOptimization ? primaryImgSrc.replace(/images.autotrader.com|assets.cai-media-management.com/, 'images2.autotrader.com') : primaryImgSrc;
    const scaledPrimaryImage = verifiedImgSrc && getScaledPrimaryImg(VEHICLE_DETAILS, browser, verifiedImgSrc, { isOptimized: enableImageOptimization });
    const sizeSet = [mainImageSizes.xs, mainImageSizes.sm, mainImageSizes.md, mainImageSizes.lg];
    const srcSet = getSrcSet(verifiedImgSrc, sizeSet, { isOptimized: enableImageOptimization });
    const openGraphImage = verifiedImgSrc && getScaledImageSource({
        src: verifiedImgSrc,
        isOptimized: enableImageOptimization,
        ...imageSizes.openGraphImg,
    });

    if (statusCode) {
        return <Error statusCode={statusCode} />;
    }

    return (
        <>
            {enablePersonalizationEngine && <PersonalizationEngineContainer pageName={VEHICLE_DETAILS} />}
            <CommonHeadContainer
                currentUrl={currentUrl}
                openGraphImage={openGraphImage}
                pageName={VEHICLE_DETAILS}
                query={query}
            />
            <Head>
                <link
                    rel="preload"
                    as="image"
                    href={scaledPrimaryImage}
                    imageSrcSet={srcSet}
                    imageSizes={`(min-width: ${breakpoints.min.lg}px) ${mainImageSizes.lg.width}px, (min-width: ${breakpoints.min.md}px) ${mainImageSizes.md.width}px, (min-width: ${breakpoints.min.sm}px) ${mainImageSizes.sm.width}px, ${mainImageSizes.xs.width}px`}
                    fetchPriority="high"
                />
            </Head>
            <FordVehicleDetailsSchema />
            <VdpPageEventsContainer />
            <VehicleDetailsPageContainer />
            <VDPAnalytics />
        </>
    );
}

VehicleDetailsPage.getInitialProps = async (ctx) => {
    await initializeVdpPageContext(ctx);

    // if the current ctx contains a status code then we need to abort the process and return it
    // to allow the page to render an error
    if (ctx.statusCode) {
        return {
            statusCode: ctx.statusCode,
        };
    }
    return {
        currentUrl: ctx.data.currentUrl,
        images: _get(ctx, 'data.listings[0].images'),
        query: ctx.query,
        dataIsland: ctx.data.dataIsland,
        browser: ctx.store.getState().browser,
    };
};

const { BonnetPageComponent } = withBonnetPage(VehicleDetailsPage, {
    reducers: [
        ...Object.values(vdpDucks),
        srpResultsDuck,
        srpReuseActiveStateDuck,
        listingsAgentDuck,
        expiredListingDuck, // TODO: BONNET - should this be added in _app as it used on both?
    ],
    modules: [
        SearchLinkModule,
        CookieConsentModule,
        CanonicalUrlModule,
        ParsedQueryModule,
        PersonalizationEngineModule,
        atcAuthModule,
    ],
});

export default BonnetPageComponent;
